<template>
    <section class="flex flex-col gap-2">
        <div class="flex items-center gap-2 flex-wrap">
            <span class="scaffold-details-small text-ink-muted">Completion · All active assessments</span>
            <BaseButton theme="neutral" size="sm" class="ml-auto" :disabled="!completion || downloading" @click="onDownloadCsv">
                <i class="bi bi-filetype-csv" aria-hidden="true"></i>
                {{ downloading ? "Preparing…" : "Download CSV" }}
            </BaseButton>
        </div>

        <BaseCard padding="md">
            <p v-if="!completion" class="body-small text-ink-muted">
                No assessments are running right now. Launch a campaign and completion across your organization shows up here.
            </p>

            <div v-else class="completion-layout">
                <div class="flex flex-col gap-4 min-w-0">
                    <div class="flex items-baseline gap-2.5 flex-wrap">
                        <span class="display-medium text-ink">{{ formatNumber(completion.submitted_pct / 100, "percent") }}</span>
                        <span class="body-small text-ink-muted">of participants have submitted</span>
                        <span v-if="windowLabel" class="details text-ink-muted ml-auto">{{ windowLabel }}</span>
                    </div>

                    <div class="completion-bar">
                        <span class="completion-fill fill-submitted" :style="{ width: widthOf(completion.submitted) }"></span>
                        <span class="completion-fill fill-in-progress" :style="{ width: widthOf(completion.in_progress) }"></span>
                    </div>

                    <div class="flex gap-4 flex-wrap details text-ink-muted">
                        <span class="legend-item"> <span class="swatch swatch-submitted"></span>Submitted {{ completion.submitted }} </span>
                        <span class="legend-item"> <span class="swatch swatch-in-progress"></span>In progress {{ completion.in_progress }} </span>
                        <span class="legend-item"> <span class="swatch swatch-not-started"></span>Not started {{ completion.not_started }} </span>
                    </div>

                    <p class="body-small text-ink-muted">{{ noteLine }}</p>
                </div>

                <div class="completion-tiles">
                    <div class="completion-tile">
                        <span class="scaffold-details-small text-ink-muted">Participants</span>
                        <span class="title-subsection text-ink">{{ completion.participants }}</span>
                        <span class="details text-ink-muted">{{ sourceLine }}</span>
                    </div>
                    <div class="completion-tile">
                        <span class="scaffold-details-small text-ink-muted">Started</span>
                        <span class="title-subsection text-ink">{{ completion.submitted + completion.in_progress }}</span>
                        <span class="details text-ink-muted">{{ formatNumber(completion.started_pct / 100, "percent") }} of invited</span>
                    </div>
                    <div class="completion-tile">
                        <span class="scaffold-details-small text-ink-muted">Raters invited</span>
                        <span class="title-subsection text-ink">{{ completion.raters_invited }}</span>
                        <span class="details text-ink-muted">{{ completion.raters_per_participant }} per participant</span>
                    </div>
                    <div class="completion-tile">
                        <span class="scaffold-details-small text-ink-muted">Raters responded</span>
                        <span class="title-subsection text-ink">{{ completion.raters_responded ?? "—" }}</span>
                        <span class="details text-ink-muted">{{ raterResponseLine }}</span>
                    </div>
                </div>
            </div>
        </BaseCard>

        <BaseNotification v-if="downloadError" theme="error" :text="downloadError" @dismiss="downloadError = null" />
    </section>
</template>

<script setup lang="ts">
import type { AdminCompletionSummary } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { downloadApiFile } from "~vue/utils/downloadApiFile";
import { formatNumber } from "~vue/utils/formatNumber";
import { logUserInteraction } from "~vue/utils/logUtils";
import { daysFromToday, formatDate } from "~vue/utils/threeSixty";
import { computed, ref } from "vue";

interface Props {
    completion: AdminCompletionSummary | null;
}

const props = defineProps<Props>();

const downloading = ref(false);
const downloadError = ref<string | null>(null);

const windowLabel = computed(() => {
    const start = formatDate(props.completion?.window_start, "short");
    const end = formatDate(props.completion?.window_end, "short");
    if (start && end) {
        return `${start} – ${end}`;
    }
    return start || end;
});

const sourceLine = computed(() => {
    const completion = props.completion;
    if (!completion) {
        return "";
    }
    const campaigns = `${completion.active_campaign_count} active ${completion.active_campaign_count === 1 ? "campaign" : "campaigns"}`;
    // "assessment" rather than "360": an individual cycle can be a self-reflection.
    const individuals = `${completion.individual_cycle_count} individual ${completion.individual_cycle_count === 1 ? "assessment" : "assessments"}`;
    return `${campaigns} · ${individuals}`;
});

const raterResponseLine = computed(() => {
    const completion = props.completion;
    if (!completion || completion.raters_responded_pct === null || completion.raters_responded_pct === undefined) {
        return "hidden by org setting";
    }
    return `${completion.raters_responded_pct}% response rate`;
});

const noteLine = computed(() => {
    const completion = props.completion;
    if (!completion) {
        return "";
    }
    const people = `${completion.not_started} ${completion.not_started === 1 ? "participant hasn't" : "participants haven't"} started.`;
    const closesAt = formatDate(completion.earliest_close_at, "short");
    const days = daysFromToday(completion.earliest_close_at);
    if (!closesAt || days === null || days < 0) {
        return people;
    }
    return `${people} The earliest campaign closes ${closesAt} — ${days} ${days === 1 ? "day" : "days"} from today.`;
});

function widthOf(count: number): string {
    const participants = props.completion?.participants ?? 0;
    // Clamped for the same reason the backend clamps its percentages: a cycle can
    // outlive its subject's eligibility, so a segment can exceed the whole.
    return participants ? `${Math.min(100, (count / participants) * 100)}%` : "0%";
}

async function onDownloadCsv() {
    if (downloading.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_COMPLETION_CSV_DOWNLOAD_CLICKED, {});
    downloading.value = true;
    downloadError.value = null;
    try {
        const downloaded = await downloadApiFile("/v1/reflections/admin/completion/export-csv", "assessment_completion.csv");
        if (!downloaded) {
            downloadError.value = "Could not build the CSV. Try again in a moment.";
        }
    } finally {
        downloading.value = false;
    }
}
</script>

<style scoped>
.completion-layout {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 40px;
}

.completion-bar {
    display: flex;
    width: 100%;
    height: 10px;
    border-radius: 999px;
    overflow: hidden;
    background: var(--color-progress-track);
}

.completion-fill {
    height: 100%;
    transition: width 0.3s;
}

.fill-submitted {
    background: var(--color-accent);
}

.fill-in-progress {
    background: var(--color-status-warning);
}

.legend-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.swatch {
    width: 8px;
    height: 8px;
    border-radius: 2px;
}

.swatch-submitted {
    background: var(--color-accent);
}

.swatch-in-progress {
    background: var(--color-status-warning);
}

.swatch-not-started {
    background: var(--color-progress-track);
}

.completion-tiles {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    align-self: start;
}

.completion-tile {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 16px 16px;
    border-radius: 10px;
    background: var(--color-surface-sunken);
}
</style>
